package com.shadowings.gero.presentation.component.explanation

import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.aspectRatio
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material3.ElevatedCard
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.OutlinedCard
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.ColorFilter
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.semantics.testTag
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.tooling.preview.Preview
import androidx.compose.ui.unit.dp
import com.shadowings.gero.data.repository.Hiero
import com.shadowings.gero.data.repository.UniliteralRepository
import com.shadowings.gero.domain.model.Characters
import com.shadowings.gero.domain.model.Faces
import com.shadowings.gero.domain.model.UniliteralExplanation
import com.shadowings.gero.domain.model.UserData
import com.shadowings.gero.presentation.component.avatars.AlynAvatar
import com.shadowings.gero.presentation.component.avatars.MaskoAvatar
import com.shadowings.gero.presentation.component.avatars.NiloAvatar
import com.shadowings.gero.presentation.component.avatars.ZetaAvatar
import com.shadowings.gero.presentation.ui.AppTheme
import com.shadowings.gero.presentation.ui.hieroglyphTint
import com.shadowings.gero.presentation.ui.titleFontFamily
import com.shadowings.gero.presentation.util.DeviceClass
import com.shadowings.gero.presentation.util.DevicePreviews
import com.shadowings.gero.presentation.util.rememberDeviceInfo
import gero.composeapp.generated.resources.Res
import gero.composeapp.generated.resources.card_title_determinativo
import gero.composeapp.generated.resources.card_title_fonogramma
import gero.composeapp.generated.resources.card_title_ideogramma
import gero.composeapp.generated.resources.hiero_detail_title
import gero.composeapp.generated.resources.mnemonic_association_hint
import gero.composeapp.generated.resources.triangle
import gero.composeapp.generated.resources.triangle_desc
import org.jetbrains.compose.resources.painterResource
import org.jetbrains.compose.resources.stringResource

@DevicePreviews
@Preview
@Preview(name = "Dark", uiMode = 0x20, showBackground = true)
@Composable
fun HieroExplanationComposablePreview() {
    AppTheme {
        Surface {
            HieroExplanationComposable(
                item = UniliteralExplanation(
                    id = "D36",
                    title = Res.string.hiero_detail_title,
                    titleArgs = listOf("braccio"),
                ),
                page = 0,
                userData = UserData(),
                hiero = listOf(
                    Hiero(
                        gardinerCode = "D36",
                        ideogramma = "𓂧",
                        determinativo = "-",
                        transliteration = "d",
                        unicode = "U+130A7",
                        mnemonico = "Il segno rappresenta un braccio umano, con la mano aperta e le dita distese.",
                        ideogrammaEn = "𓂧",
                        determinativoEn = "-",
                        mnemonicoEn = "The sign represents a human arm with the hand open and fingers extended."
                    )
                )
            )
        }
    }
}

@Composable
fun HieroExplanationComposable(
    item: UniliteralExplanation,
    page: Int,
    hiero: List<Hiero>,
    userData: UserData,
    languageCode: String = "en",
    fillMaxHeight: Boolean = true
) {
    val hiero = hiero.first { item.id == it.gardinerCode }
    val deviceInfo = rememberDeviceInfo()

    val ideogramma = hiero.getIdeogramma(languageCode)
    val determinativo = hiero.getDeterminativo(languageCode)
    val mnemonico = hiero.getMnemonico(languageCode)

    BoxWithConstraints(
        modifier = if (fillMaxHeight) Modifier.fillMaxSize() else Modifier.fillMaxWidth()
    ) {
        val isLandscape = maxWidth > maxHeight
        val isPhoneLargePortrait = !isLandscape && maxWidth >= 400.dp && maxWidth < 600.dp

        ElevatedCard(
            modifier = Modifier
                .then(if (fillMaxHeight) Modifier.fillMaxSize() else Modifier.fillMaxWidth())
                .padding(
                    horizontal = if (isLandscape) 8.dp else (if (fillMaxHeight) 16.dp else 8.dp),
                    vertical = if (isLandscape) 4.dp else (if (fillMaxHeight) 16.dp else 4.dp)
                )
                .semantics { testTag = "explanation_page_$page" },
        ) {
            Column(
                modifier = Modifier
                    .then(if (fillMaxHeight) Modifier.fillMaxSize() else Modifier.fillMaxWidth())
                    .padding(
                        horizontal = if (isLandscape) 8.dp else 16.dp,
                        vertical = if (isLandscape) 8.dp else 16.dp
                    ),
                verticalArrangement = Arrangement.Center,
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                if (isLandscape) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(16.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        // Left Column: Hieroglyph Image + Avatar
                        Column(
                            modifier = Modifier.weight(1f),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            UniliteralRepository.resFromGardinerCode(item.id)?.let { res ->
                                Image(
                                    modifier = Modifier.size(72.dp).padding(4.dp),
                                    painter = painterResource(res),
                                    contentDescription = if (ideogramma != "-") ideogramma else determinativo,
                                    colorFilter = ColorFilter.tint(hieroglyphTint)
                                )
                            }

                            val fontScale = LocalDensity.current.fontScale

                            if (fontScale < 1.49f) {
                                when (item.character) {
                                    Characters.Nilo -> {
                                        NiloAvatar(
                                            face = Faces.Idle,
                                            userData = userData
                                        )
                                    }

                                    Characters.Zeta -> {
                                        ZetaAvatar(
                                            face = Faces.Idle,
                                            userData = userData
                                        )
                                    }

                                    Characters.Masko -> {
                                        MaskoAvatar(
                                            face = Faces.Idle,
                                            userData = userData
                                        )
                                    }

                                    else -> AlynAvatar(
                                        face = Faces.Idle,
                                        userData = userData
                                    )
                                }
                            }
                        }

                        // Right Column: Type cards (horizontal Row) + bubble
                        Column(
                            modifier = Modifier.weight(2.5f),
                            horizontalAlignment = Alignment.CenterHorizontally,
                            verticalArrangement = Arrangement.spacedBy(8.dp)
                        ) {
                            // Row of indicator cards
                            Row(
                                modifier = Modifier.fillMaxWidth(),
                                horizontalArrangement = Arrangement.spacedBy(8.dp)
                            ) {
                                if (ideogramma != "-") {
                                    OutlinedCard(modifier = Modifier.weight(1f)) {
                                        Column(
                                            modifier = Modifier.fillMaxWidth().padding(10.dp),
                                            horizontalAlignment = Alignment.CenterHorizontally
                                        ) {
                                            Text(
                                                text = stringResource(Res.string.card_title_ideogramma),
                                                style = MaterialTheme.typography.bodySmall,
                                                color = MaterialTheme.colorScheme.primary
                                            )
                                            Text(
                                                text = ideogramma,
                                                style = MaterialTheme.typography.titleMedium.copy(fontFamily = titleFontFamily()),
                                                textAlign = TextAlign.Center
                                            )
                                        }
                                    }
                                }

                                if (determinativo != "-") {
                                    OutlinedCard(modifier = Modifier.weight(1f)) {
                                        Column(
                                            modifier = Modifier.fillMaxWidth().padding(10.dp),
                                            horizontalAlignment = Alignment.CenterHorizontally
                                        ) {
                                            Text(
                                                text = stringResource(Res.string.card_title_determinativo),
                                                style = MaterialTheme.typography.bodySmall,
                                                color = MaterialTheme.colorScheme.primary
                                            )
                                            Text(
                                                text = determinativo,
                                                style = MaterialTheme.typography.titleMedium.copy(fontFamily = titleFontFamily()),
                                                textAlign = TextAlign.Center
                                            )
                                        }
                                    }
                                }

                                if (hiero.transliteration != "-") {
                                    OutlinedCard(modifier = Modifier.weight(1f)) {
                                        Column(
                                            modifier = Modifier.fillMaxWidth().padding(10.dp),
                                            horizontalAlignment = Alignment.CenterHorizontally
                                        ) {
                                            Text(
                                                text = stringResource(Res.string.card_title_fonogramma),
                                                style = MaterialTheme.typography.bodySmall,
                                                color = MaterialTheme.colorScheme.primary
                                            )
                                            Text(
                                                text = hiero.transliteration,
                                                style = MaterialTheme.typography.titleMedium
                                            )
                                        }
                                    }
                                }
                            }

                            val feedbackText = if (mnemonico != "-") {
                                mnemonico
                            } else {
                                stringResource(Res.string.mnemonic_association_hint)
                            }

                            OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                                Text(
                                    modifier = Modifier.padding(8.dp),
                                    text = feedbackText,
                                    style = MaterialTheme.typography.bodyMedium,
                                    textAlign = TextAlign.Center
                                )
                            }
                        }
                    }
                } else {
                    UniliteralRepository.resFromGardinerCode(item.id)?.let { res ->
                        Image(
                            modifier = Modifier.fillMaxWidth(0.3f).aspectRatio(1f).padding(8.dp),
                            painter = painterResource(res),
                            contentDescription = if (ideogramma != "-") ideogramma else determinativo,
                            colorFilter = ColorFilter.tint(hieroglyphTint)
                        )
                    }

                    Column(
                        modifier = Modifier.fillMaxWidth().padding(vertical = 8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        // 1. CARD IDEOGRAMMA
                        if (ideogramma != "-") {
                            OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                                Column(
                                    modifier = Modifier.fillMaxWidth().padding(8.dp),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(
                                        text = stringResource(Res.string.card_title_ideogramma),
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                    Text(
                                        text = ideogramma,
                                        style = MaterialTheme.typography.titleLarge.copy(fontFamily = titleFontFamily()),
                                        textAlign = TextAlign.Center
                                    )
                                }
                            }
                        }

                        // 2. CARD DETERMINATIVO
                        if (determinativo != "-") {
                            OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                                Column(
                                    modifier = Modifier.fillMaxWidth().padding(8.dp),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(
                                        text = stringResource(Res.string.card_title_determinativo),
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                    Text(
                                        text = determinativo,
                                        style = MaterialTheme.typography.titleLarge.copy(fontFamily = titleFontFamily()),
                                        textAlign = TextAlign.Center
                                    )
                                }
                            }
                        }

                        // 3. CARD FONOGRAMMA
                        if (hiero.transliteration != "-") {
                            OutlinedCard(modifier = Modifier.fillMaxWidth()) {
                                Column(
                                    modifier = Modifier.fillMaxWidth().padding(8.dp),
                                    horizontalAlignment = Alignment.CenterHorizontally
                                ) {
                                    Text(
                                        text = stringResource(Res.string.card_title_fonogramma),
                                        style = MaterialTheme.typography.bodyMedium,
                                        color = MaterialTheme.colorScheme.primary
                                    )
                                    Text(
                                        text = hiero.transliteration,
                                        style = MaterialTheme.typography.titleLarge
                                    )
                                }
                            }
                        }
                    }

                    val feedbackText = if (mnemonico != "-") {
                        mnemonico
                    } else {
                        stringResource(Res.string.mnemonic_association_hint)
                    }

                    val fontScale = LocalDensity.current.fontScale
                    val deviceInfo = rememberDeviceInfo()
                    val avatarSize = when (deviceInfo.deviceClass) {
                        DeviceClass.PHONE_SMALL -> 64.dp
                        DeviceClass.PHONE_MEDIUM -> 80.dp
                        DeviceClass.PHONE_LARGE -> if (isPhoneLargePortrait) 120.dp else 110.dp
                        DeviceClass.TABLET_MEDIUM -> 120.dp
                        DeviceClass.TABLET_LARGE -> 150.dp
                        DeviceClass.WEB_FULL -> 150.dp
                    }
                    if (fontScale < 1.49f) {
                        when (item.character) {
                            Characters.Nilo -> {
                                NiloAvatar(
                                    face = Faces.Idle,
                                    userData = userData
                                )
                            }

                            Characters.Zeta -> {
                                ZetaAvatar(
                                    face = Faces.Idle,
                                    userData = userData
                                )
                            }

                            Characters.Masko -> {
                                MaskoAvatar(
                                    face = Faces.Idle,
                                    userData = userData
                                )
                            }

                            else -> AlynAvatar(
                                face = Faces.Idle,
                                userData = userData
                            )
                        }
                        Spacer(modifier = Modifier.height(8.dp))
                    }
                    Column(horizontalAlignment = Alignment.CenterHorizontally) {
                        Image(
                            modifier = Modifier.width(8.dp),
                            painter = painterResource(Res.drawable.triangle),
                            contentDescription = stringResource(Res.string.triangle_desc),
                            colorFilter = ColorFilter.tint(MaterialTheme.colorScheme.outlineVariant)
                        )
                        OutlinedCard {
                            Text(
                                modifier = Modifier.padding(16.dp),
                                text = feedbackText,
                                style = if (deviceInfo.deviceClass == DeviceClass.PHONE_SMALL) {
                                    MaterialTheme.typography.bodyMedium
                                } else {
                                    MaterialTheme.typography.bodyLarge
                                },
                                textAlign = TextAlign.Center
                            )
                        }
                    }
                }
            }
        }
    }
}
